// Registry of navigable pages. The assistant uses this to resolve a user
// request ("take me to careers") into a real route, and the widget uses it to
// perform the navigation. Keep in sync with the <Routes> in App.tsx.

export interface PageEntry {
  path: string
  label: string
  /** Keywords/aliases used for intent matching. */
  keywords: string[]
  /** One-line description the assistant can read aloud. */
  summary: string
}

export const PAGES: PageEntry[] = [
  {
    path: '/',
    label: 'Home',
    keywords: ['home', 'homepage', 'start', 'main', 'landing'],
    summary:
      'The BigData Ghana home page — an overview of our geospatial intelligence, data analytics, AI and cloud solutions.',
  },
  {
    path: '/services',
    label: 'Services',
    keywords: ['services', 'what you do', 'offerings', 'capabilities'],
    summary:
      'Our services: geospatial intelligence, data analytics, AI and automation, and cloud platforms.',
  },
  {
    path: '/geospatial',
    label: 'Geospatial Intelligence',
    keywords: ['geospatial', 'gis', 'mapping', 'earth observation', 'satellite', 'remote sensing', 'maps'],
    summary:
      'Geospatial intelligence and earth observation — turning maps and satellite data into decisions grounded in Ghana.',
  },
  {
    path: '/cloud-platforms',
    label: 'Cloud Platforms',
    keywords: ['cloud', 'aws', 'infrastructure', 'cloud platforms', 'hosting', 'devops'],
    summary: 'Cloud platforms and infrastructure — we are an AWS Partner with certified cloud engineers.',
  },
  {
    path: '/ai-automation',
    label: 'AI & Automation',
    keywords: ['ai', 'artificial intelligence', 'automation', 'machine learning', 'ml', 'bigconnect'],
    summary: 'AI and automation solutions, including our BigConnect AI communication platform.',
  },
  {
    path: '/data-analytics',
    label: 'Data Analytics',
    keywords: ['data analytics', 'analytics', 'business intelligence', 'bi', 'dashboards', 'data'],
    summary: 'Data analytics and business intelligence — dashboards and insight from your data.',
  },
  {
    path: '/proof',
    label: 'Proof & Projects',
    keywords: ['proof', 'projects', 'work', 'portfolio', 'case studies', 'track record'],
    summary: 'Proof of our work — real projects delivered across government, banking, agriculture and logistics.',
  },
  {
    path: '/solutions',
    label: 'Solutions',
    keywords: ['solutions', 'products', 'platforms'],
    summary: 'Our solutions and flagship products.',
  },
  {
    path: '/insights',
    label: 'Insights',
    keywords: ['insights', 'blog', 'articles', 'news', 'resources', 'reading'],
    summary: 'Insights — articles and thinking from the BigData Ghana team.',
  },
  {
    path: '/about',
    label: 'About',
    keywords: ['about', 'who you are', 'company', 'story', 'mission', 'vision', 'history'],
    summary:
      'About BigData Ghana — a Ghanaian technology and intelligence company founded in 2016 with eight years of spatial data.',
  },
  {
    path: '/team',
    label: 'Team',
    keywords: ['team', 'people', 'staff', 'meet the team', 'leadership', 'employees'],
    summary: 'Meet the people behind BigData Ghana — a focused, senior team of 15+.',
  },
  {
    path: '/careers',
    label: 'Careers',
    keywords: ['careers', 'jobs', 'hiring', 'work with us', 'vacancies', 'openings', 'join'],
    summary: 'Careers at BigData Ghana — open roles and what it is like to work here.',
  },
  {
    path: '/contact',
    label: 'Contact',
    keywords: ['contact', 'reach', 'email', 'phone', 'get in touch', 'talk to', 'enquiry', 'inquiry'],
    summary: 'Contact BigData Ghana — get in touch with our team.',
  },
  {
    path: '/privacy',
    label: 'Privacy Policy',
    keywords: ['privacy', 'policy', 'data protection', 'terms'],
    summary: 'Our privacy policy and how we handle data.',
  },
]

/** Find the best-matching page for a free-text request, or null. */
export function matchPage(text: string): PageEntry | null {
  const q = text.toLowerCase()
  let best: { page: PageEntry; score: number } | null = null

  for (const page of PAGES) {
    let score = 0
    for (const kw of page.keywords) {
      if (q.includes(kw)) score = Math.max(score, kw.length)
    }
    if (q.includes(page.label.toLowerCase())) score = Math.max(score, page.label.length + 1)
    if (score > 0 && (!best || score > best.score)) best = { page, score }
  }

  return best?.page ?? null
}
